import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Key, Loader2, Package, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CopyId, PageHeader, StatusPill } from '@/components/page';
import {
  AUTH_METHODS,
  Callout,
  DevLabel,
  EmptyState,
  GrantPicker,
  Hint,
  InlineError,
  Mono,
  ScopePicker,
  SecretDialog,
  SecretField,
  Tag,
  UriListEditor,
  validateUriList,
} from '@/components/oauth';
import { useCreateClient, useProducts, useToast } from '@/hooks';
import { cn } from '@/lib/utils';
import type { ClientType, GrantType, TokenEndpointAuthMethod } from '@/types';

const STEPS = ['App & name', 'Client type', 'Redirects & scopes', 'Review'] as const;

interface FormState {
  product_id: string;
  name: string;
  client_type: ClientType;
  token_endpoint_auth_method: TokenEndpointAuthMethod;
  pkce_required: boolean;
  allowed_grant_types: GrantType[];
  redirect_uris: string[];
  allowed_scopes: string[];
}

export function ClientNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const createClient = useCreateClient();

  const pre = searchParams.get('product') || '';
  const [step, setStep] = useState(1);
  const [reached, setReached] = useState(1);
  const [form, setForm] = useState<FormState>({
    product_id: '',
    name: '',
    client_type: 'public',
    token_endpoint_auth_method: 'none',
    pkce_required: true,
    allowed_grant_types: ['authorization_code', 'refresh_token'],
    redirect_uris: [''],
    allowed_scopes: ['openid', 'profile', 'email'],
  });
  const [errors, setErrors] = useState<{
    app?: string;
    name?: string;
    grants?: string;
    scopes?: string;
    uris: Record<number, string>;
  }>({ uris: {} });
  const [created, setCreated] = useState<{ id: string; name: string; clientId: string; secret: string } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Preselect the app from ?product= once products load (active apps only).
  const preApplied = useRef(false);
  useEffect(() => {
    if (preApplied.current || !products.length || !pre) return;
    preApplied.current = true;
    const p = products.find((x) => x.id === pre);
    if (p && p.is_active) setForm((f) => ({ ...f, product_id: p.id }));
    else if (p) toast({ variant: 'destructive', title: 'That app is inactive', description: 'Choose an active app for the new client.' });
  }, [products, pre, toast]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const codeFlow = form.allowed_grant_types.includes('authorization_code');
  const pub = form.client_type === 'public';
  const product = products.find((p) => p.id === form.product_id);

  const chooseType = (t: ClientType) => {
    setForm((f) => {
      if (t === 'public')
        return {
          ...f,
          client_type: t,
          token_endpoint_auth_method: 'none',
          pkce_required: true,
          allowed_grant_types: f.allowed_grant_types.filter((g) => g !== 'client_credentials'),
        };
      return {
        ...f,
        client_type: t,
        token_endpoint_auth_method:
          f.token_endpoint_auth_method === 'none' ? 'client_secret_basic' : f.token_endpoint_auth_method,
      };
    });
  };

  /** Validate one step; returns true when it passes. */
  const validate = (n: number, show = true): boolean => {
    if (n === 1) {
      const app = form.product_id ? '' : 'Choose the app this client belongs to.';
      const name = form.name.trim() ? '' : 'Enter a client name.';
      if (show) setErrors((e) => ({ ...e, app, name }));
      return !app && !name;
    }
    if (n === 2) {
      const grants = form.allowed_grant_types.length ? '' : 'Choose at least one grant type.';
      if (show) setErrors((e) => ({ ...e, grants }));
      return !grants;
    }
    if (n === 3) {
      const uris = validateUriList(form.redirect_uris, form.client_type, codeFlow);
      const scopes = form.allowed_scopes.length ? '' : 'Choose at least one scope.';
      if (show) setErrors((e) => ({ ...e, uris, scopes }));
      return Object.keys(uris).length === 0 && !scopes;
    }
    return true;
  };

  const go = (n: number) => {
    if (n > step) {
      for (let k = step; k < n; k++) {
        if (!validate(k)) {
          if (k !== step) setStep(k);
          return;
        }
      }
    }
    setStep(n);
    setReached((r) => Math.max(r, n));
    window.setTimeout(() => headingRef.current?.focus(), 0);
  };

  const create = async () => {
    const bad = [1, 2, 3].find((n) => !validate(n, false));
    if (bad) {
      setStep(bad);
      validate(bad, true);
      return;
    }
    try {
      const result = await createClient.mutateAsync({
        ...form,
        name: form.name.trim(),
        redirect_uris: form.redirect_uris.map((u) => u.trim()).filter(Boolean),
      });
      if (result.client_secret) {
        setCreated({ id: result.id, name: result.name, clientId: result.client_id, secret: result.client_secret });
      } else {
        toast({ title: 'Client created', description: `${result.name} is ready to use.` });
        navigate(`/clients/${result.id}`);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not create client',
        description: error instanceof Error ? error.message : 'Failed to create client',
      });
    }
  };

  if (!productsLoading && products.length === 0) {
    return (
      <div>
        <PageHeader title="New OAuth client" />
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={<Package />}
            title="Create an app first"
            action={
              <Button asChild>
                <Link to="/products/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New app
                </Link>
              </Button>
            }
          >
            Every OAuth client belongs to an app. Create the app, then add clients for each
            surface — web, mobile, CLI or server.
          </EmptyState>
        </div>
      </div>
    );
  }

  const EditLink = ({ to }: { to: number }) => (
    <button type="button" onClick={() => go(to)} className="text-xs text-primary hover:underline">
      Edit
    </button>
  );

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            <Key className="h-3.5 w-3.5" />
            OAuth clients
          </>
        }
        title="New OAuth client"
        description="Register an app surface that signs users in through authrz.me."
      />

      <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)] md:items-start">
        <nav aria-label="Steps" className="flex gap-1 overflow-x-auto md:sticky md:top-20 md:flex-col">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const on = n === step;
            const done = n < reached && !on;
            return (
              <button
                key={label}
                type="button"
                disabled={n > reached}
                aria-current={on ? 'step' : undefined}
                onClick={() => go(n)}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
                  on && 'bg-foreground/[0.06] font-semibold text-foreground'
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full border border-border font-mono text-[11px]',
                    on && 'border-foreground bg-foreground text-background',
                    done && 'border-success text-success'
                  )}
                >
                  {done ? <Check className="h-3 w-3" /> : n}
                </span>
                {label}
              </button>
            );
          })}
        </nav>

        <form
          noValidate
          className="min-w-0 overflow-hidden rounded-lg border border-border bg-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (step < 4) go(step + 1);
            else void create();
          }}
        >
          <div className="border-b border-border px-4 py-4 sm:px-[18px]">
            <h2 ref={headingRef} tabIndex={-1} className="text-[17px] font-semibold tracking-tight outline-none">
              {STEPS[step - 1]}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {
                [
                  'Which app is this client for, and what should users see when they sign in?',
                  'Can this app keep a secret? Type can’t be changed later.',
                  'Where authrz.me may send users back, and what this client may ask for.',
                  'Check the details, then create the client.',
                ][step - 1]
              }
            </p>
          </div>

          <div className="space-y-5 p-4 sm:p-[18px]">
            {step === 1 && (
              <>
                <div className="space-y-1.5">
                  <DevLabel htmlFor="fApp">App (required)</DevLabel>
                  <Select
                    value={form.product_id}
                    onValueChange={(v) => {
                      set('product_id', v);
                      setErrors((e) => ({ ...e, app: '' }));
                    }}
                  >
                    <SelectTrigger id="fApp" aria-invalid={errors.app ? true : undefined}>
                      <SelectValue placeholder={productsLoading ? 'Loading…' : 'Choose an app…'} />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id} disabled={!p.is_active}>
                          {p.name}
                          {p.organization_name ? ` · ${p.organization_name}` : ''}
                          {p.is_active ? '' : ' (inactive)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Hint>Tokens issued to this client carry the app’s roles and permissions.</Hint>
                  <InlineError>{errors.app}</InlineError>
                </div>
                <div className="space-y-1.5">
                  <DevLabel htmlFor="fName">Client name (required)</DevLabel>
                  <Input
                    id="fName"
                    value={form.name}
                    maxLength={80}
                    placeholder="e.g. Billing dashboard, iOS app, Deploy CLI"
                    aria-invalid={errors.name ? true : undefined}
                    onChange={(e) => set('name', e.target.value)}
                    onBlur={(e) =>
                      setErrors((er) => ({ ...er, name: e.target.value.trim() ? '' : 'Enter a client name.' }))
                    }
                  />
                  <Hint>Shown on the consent screen. Name it after the surface, not the company.</Hint>
                  <InlineError>{errors.name}</InlineError>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <fieldset className="grid gap-3 sm:grid-cols-2">
                  <legend className="sr-only">Client type</legend>
                  {(
                    [
                      {
                        value: 'public' as const,
                        title: 'Public',
                        sub: 'Single-page apps, mobile and desktop apps, CLIs.',
                        points: [
                          'No secret — every sign-in uses PKCE (S256)',
                          'Custom URI schemes and loopback redirects allowed',
                          'Token auth: none',
                        ],
                      },
                      {
                        value: 'confidential' as const,
                        title: 'Confidential',
                        sub: 'Server-rendered apps, back ends, workers.',
                        points: [
                          'Authenticates with a client secret',
                          'Client credentials grant available',
                          'Secret is shown once after creation',
                        ],
                      },
                    ]
                  ).map((o) => (
                    <label
                      key={o.value}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3.5 hover:border-foreground/30',
                        form.client_type === o.value && 'border-foreground/50 bg-foreground/[0.03]'
                      )}
                    >
                      <input
                        type="radio"
                        name="fType"
                        value={o.value}
                        checked={form.client_type === o.value}
                        onChange={() => chooseType(o.value)}
                        className="mt-1 accent-[hsl(var(--primary))]"
                      />
                      <span className="min-w-0">
                        <b className="block text-[14px] font-semibold">{o.title}</b>
                        <small className="block text-[12.5px] text-muted-foreground">{o.sub}</small>
                        <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[12.5px] text-muted-foreground">
                          {o.points.map((p) => (
                            <li key={p}>{p}</li>
                          ))}
                        </ul>
                      </span>
                    </label>
                  ))}
                </fieldset>

                {!pub && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <DevLabel htmlFor="fAuth">Token endpoint auth</DevLabel>
                      <Select
                        value={form.token_endpoint_auth_method}
                        onValueChange={(v: TokenEndpointAuthMethod) => set('token_endpoint_auth_method', v)}
                      >
                        <SelectTrigger id="fAuth">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(['client_secret_basic', 'client_secret_post'] as const).map((m) => (
                            <SelectItem key={m} value={m}>
                              {AUTH_METHODS[m]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Hint>Basic is the default for most server libraries.</Hint>
                    </div>
                    <div className="space-y-1.5">
                      <DevLabel>PKCE</DevLabel>
                      <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                        <div>
                          <b className="text-[13.5px] font-semibold">Require PKCE</b>
                          <p className="text-[12.5px] text-muted-foreground">Recommended even with a secret.</p>
                        </div>
                        <Switch
                          checked={form.pkce_required}
                          onCheckedChange={(v) => set('pkce_required', v)}
                          aria-label="Require PKCE"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <DevLabel>Grant types</DevLabel>
                  <GrantPicker
                    value={form.allowed_grant_types}
                    clientType={form.client_type}
                    onChange={(v) => {
                      set('allowed_grant_types', v);
                      setErrors((e) => ({ ...e, grants: '' }));
                    }}
                  />
                  <InlineError>{errors.grants}</InlineError>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="space-y-1.5">
                  <DevLabel>
                    Redirect URIs{' '}
                    <span className="normal-case tracking-normal">
                      {codeFlow ? '(at least one)' : '(not used without the authorization code grant)'}
                    </span>
                  </DevLabel>
                  <UriListEditor
                    uris={form.redirect_uris}
                    onChange={(u) => {
                      set('redirect_uris', u);
                      setErrors((e) => ({ ...e, uris: {} }));
                    }}
                    clientType={form.client_type}
                    errors={errors.uris}
                  />
                  <Hint>
                    {pub
                      ? 'https, http on localhost/127.0.0.1, or a reverse-domain scheme like com.example.app://callback.'
                      : 'https only; http is accepted for localhost.'}{' '}
                    Exact match — no wildcards.
                  </Hint>
                </div>
                <div className="space-y-1.5">
                  <DevLabel>Allowed scopes</DevLabel>
                  <ScopePicker
                    value={form.allowed_scopes}
                    onChange={(v) => {
                      set('allowed_scopes', v);
                      setErrors((e) => ({ ...e, scopes: '' }));
                    }}
                  />
                  <InlineError>{errors.scopes}</InlineError>
                </div>
                <p className="text-[12.5px] text-muted-foreground">
                  Logout URIs can be added on the client’s Logout tab after creation.
                </p>
              </>
            )}

            {step === 4 && (
              <>
                <dl>
                  {(
                    [
                      ['App', 1, <span key="a">{product?.name || '—'}{product?.organization_name && <span className="text-muted-foreground"> · {product.organization_name}</span>}</span>],
                      ['Name', 1, <span key="n">{form.name}</span>],
                      ['Type', 2, <span key="t" className="flex flex-wrap items-center gap-2"><Tag>{form.client_type}</Tag><Mono className="text-muted-foreground">{form.token_endpoint_auth_method}</Mono></span>],
                      ['PKCE', null, form.pkce_required ? <StatusPill key="p" tone="ok">Required · S256</StatusPill> : <StatusPill key="p" tone="warn">Optional</StatusPill>],
                      ['Grant types', 2, <span key="g" className="flex flex-wrap gap-1">{form.allowed_grant_types.map((g) => <Tag key={g} solid>{g}</Tag>)}</span>],
                      ['Redirect URIs', 3, <span key="r" className="grid gap-1">{form.redirect_uris.filter((u) => u.trim()).map((u) => <Mono key={u} className="break-all">{u}</Mono>) as React.ReactNode}{!form.redirect_uris.some((u) => u.trim()) && <span className="text-muted-foreground">None</span>}</span>],
                      ['Scopes', 3, <span key="s" className="flex flex-wrap gap-1">{form.allowed_scopes.map((s) => <Tag key={s}>{s}</Tag>)}</span>],
                    ] as [string, number | null, React.ReactNode][]
                  ).map(([k, editStep, v]) => (
                    <div
                      key={k}
                      className="grid grid-cols-1 gap-x-4 gap-y-1 border-t border-border py-2.5 first:border-t-0 first:pt-0 sm:grid-cols-[170px_minmax(0,1fr)]"
                    >
                      <dt className="text-[13px] text-muted-foreground">{k}</dt>
                      <dd className="flex min-w-0 items-start justify-between gap-3 break-words">
                        <div className="min-w-0">{v}</div>
                        {editStep && <EditLink to={editStep} />}
                      </dd>
                    </div>
                  ))}
                </dl>
                {!pub && (
                  <Callout tone="warn" title="The secret is shown once">
                    After creating, copy the client secret into your server’s secret store before
                    closing the dialog.
                  </Callout>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3 sm:px-[18px]">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={() => go(step - 1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            ) : (
              <Button type="button" variant="outline" asChild>
                <Link to="/clients">Cancel</Link>
              </Button>
            )}
            {step < 4 ? (
              <Button type="submit">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button type="submit" disabled={createClient.isPending}>
                {createClient.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                {createClient.isPending ? 'Creating…' : 'Create client'}
              </Button>
            )}
          </div>
        </form>
      </div>

      <SecretDialog
        open={!!created}
        description={`${created?.name ?? 'This client'} authenticates to the token endpoint with this secret. authrz.me stores only a hash.`}
        onDone={() => {
          const id = created?.id;
          setCreated(null);
          if (id) navigate(`/clients/${id}`);
        }}
      >
        {created && (
          <div className="space-y-3">
            <div className="space-y-1">
              <DevLabel>Client ID</DevLabel>
              <CopyId value={created.clientId} className="py-1" />
            </div>
            <SecretField label="Client secret" value={created.secret} />
          </div>
        )}
      </SecretDialog>
    </div>
  );
}
